import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { AutoFixHigh as ImproveIcon } from '@mui/icons-material';
import { Plan, TargetDetail, TargetType, getLearningStatus, learningApi, learningPath } from '../../services/learningApi';
import { fmtCredits } from '../../services/testsApi';

type TokenGetter = () => Promise<string | null | undefined>;

/** "Improve with Learning": a learning analysis that starts from the failing test cases.
 * Hidden unless Learning is available to the account. */
export const ImproveWithLearning: React.FC<{
  getToken: TokenGetter;
  targetType: TargetType;
  targetId: string;
  targetName: string;
  failing: number;
  size?: 'small' | 'medium';
}> = ({ getToken, targetType, targetId, targetName, failing, size = 'medium' }) => {
  const api = useMemo(() => learningApi(getToken), [getToken]);
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<TargetDetail | null>(null);
  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(false);
  const [starting, setStarting] = useState(false);
  const [started, setStarted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getLearningStatus(getToken).then(s => !cancelled && setAvailable(!!s.enabled && !s.blocked_reason));
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPlan(null);
    setStarted(false);
    api
      .target(targetType, targetId)
      .then(async d => {
        if (cancelled) return;
        setDetail(d);
        if (d.config?.enabled && !d.active_cycle) {
          const p = await api.preview(targetType, targetId);
          if (!cancelled) setPlan(p);
        }
      })
      .catch(e => !cancelled && setError(e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, api, targetType, targetId]);

  if (!available || failing <= 0) return null;

  const start = async () => {
    setStarting(true);
    setError(null);
    try {
      await api.startCycle(targetType, targetId);
      setStarted(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setStarting(false);
    }
  };

  const on = !!detail?.config?.enabled;
  const usesTests = !!detail?.settings.signals.enabled.includes('test_results');
  const tests = plan?.tests_planned || { failed: 0, passed: 0 };
  const realRuns = plan ? Math.max(0, plan.planned - tests.failed - tests.passed) : 0;
  const openLink = (tab?: string) => (
    <Button component={RouterLink} to={learningPath(targetType, targetId, tab)} onClick={() => setOpen(false)}>
      Open Learning
    </Button>
  );

  return (
    <>
      <Button variant="outlined" size={size} startIcon={<ImproveIcon />} onClick={() => setOpen(true)}>
        Improve with Learning
      </Button>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Improve {targetName} from its test failures</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Learning reads the failing test cases (what was expected, and why the grader failed them) along with real runs, then suggests
            instruction fixes you can review and apply. Nothing changes until you apply a suggestion.
          </Typography>
          {loading && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <CircularProgress size={28} />
            </Box>
          )}
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          {!loading && detail && !on && (
            <Alert severity="info">
              Learning isn't turned on for {targetName} yet. Turn it on from the Learning page, where you choose what it may change; then come back
              here.
            </Alert>
          )}
          {!loading && on && !usesTests && (
            <Alert severity="info">
              Test results are switched off for this target. Turn them on in Learning → Settings → Show advanced settings → How to judge runs.
            </Alert>
          )}
          {!loading && on && detail?.active_cycle && <Alert severity="info">An analysis is already running. Its results show on the Learning page.</Alert>}
          {!loading && plan && !started && usesTests && (
            <Stack spacing={1.5}>
              {tests.failed > 0 ? (
                <Alert severity="success" icon={false}>
                  This analysis includes <b>{tests.failed}</b> failing test case{tests.failed === 1 ? '' : 's'}
                  {tests.passed ? <>, <b>{tests.passed}</b> passing as good examples,</> : ''} and <b>{realRuns}</b> real run{realRuns === 1 ? '' : 's'}.
                </Alert>
              ) : (
                <Alert severity="warning">
                  No new failing test results to learn from. They were already analyzed, or the tests ran before the instructions last changed.
                  Run the tests again, then come back.
                </Alert>
              )}
              <Typography variant="body2">
                Estimated cost: about <b>{fmtCredits(plan.estimate.total_credits)}</b> credits (stops at {fmtCredits(plan.credit_cap)}).
              </Typography>
              {plan.blockers.map(b => (
                <Alert key={b} severity="warning">{b}</Alert>
              ))}
            </Stack>
          )}
          {started && (
            <Alert severity="success">
              Analysis started. In a few minutes its suggestions appear on the Learning page. Apply the ones you agree with, then run these tests
              again to check the fix.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{started ? 'Close' : 'Cancel'}</Button>
          {detail && !on && (
            <Button variant="contained" component={RouterLink} to={learningPath(targetType, targetId, 'settings')} onClick={() => setOpen(false)}>
              Set up Learning
            </Button>
          )}
          {on && !usesTests && openLink('settings')}
          {on && (detail?.active_cycle || started) && openLink(started ? 'suggestions' : undefined)}
          {plan && !started && usesTests && (
            <Button variant="contained" onClick={start} disabled={starting || !plan.can_run || tests.failed === 0}>
              {starting ? 'Starting…' : 'Start analysis'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
